/**
 * Admin screen — Cost Dashboard and Beta signups.
 * Access guard: identity.sub === VITE_ADMIN_SUB
 */

import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { getUserIdentity, type UserIdentity } from '../lib/auth';
import {
  fetchUsage, fetchCartesiaCredits,
  fetchBetaSignups, approveBetaUsers,
  type UserUsageRow, type OperationType, type UsageResponse, type CartesiaCreditsResponse,
  type BetaSignup,
} from '../lib/admin-api';

const ADMIN_SUB = import.meta.env.VITE_ADMIN_SUB as string | undefined;

type AdminTab = 'usage' | 'beta';
type FilterMode = 'this-month' | 'last-month' | 'this-year' | 'last-year' | 'custom';

const OPERATION_COLUMNS: { key: OperationType; label: string }[] = [
  { key: 'interview_turn',         label: 'Interview Turns' },
  { key: 'session_title',          label: 'Titles' },
  { key: 'book_generation',        label: 'Book Gen' },
  { key: 'book_outline',           label: 'Outline' },
  { key: 'book_ordering',          label: 'Book Order' },
  { key: 'outline_refine',         label: 'Outline Refine' },
  { key: 'book_section_generation', label: 'Section AI' },
  { key: 'session_classification', label: 'Classification' },
  { key: 'session_summary',        label: 'Summaries' },
  { key: 'ai_edit_rewrite',        label: 'AI Edits' },
  { key: 'document_extract',       label: 'Doc Extract' },
  { key: 'voice_session',          label: 'Voice Sessions' },
  { key: 'voice_minutes',          label: 'Voice Minutes' },
  { key: 'audio_upload',           label: 'Audio Uploads' },
  { key: 'photo_upload',           label: 'Photos' },
  { key: 'pdf_export',             label: 'PDF Export' },
  { key: 'cover_pdf_export',       label: 'Cover PDF' },
  { key: 'toc_generation',         label: 'TOC' },
  { key: 'index_generation',       label: 'Index' },
  { key: 'cover_design',           label: 'Cover Design' },
  { key: 'job_failed',             label: 'Failed Jobs' },
  { key: 'cartesia_tts',           label: 'Cartesia TTS' },
  { key: 'polly_tts',              label: 'Polly TTS (legacy)' },
  { key: 'transcribe_stt',         label: 'Transcribe STT' },
];

function formatUsd(n: number): string {
  return `$${n.toFixed(4)}`;
}

function formatInt(n: number): string {
  return Math.round(n).toLocaleString();
}

function formatMinutes(seconds: number): string {
  return `${(seconds / 60).toFixed(1)}m`;
}

function formatMegabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getTotals(row: UserUsageRow) {
  const breakdowns = Object.values(row.breakdown).filter(Boolean) as NonNullable<UserUsageRow['breakdown'][OperationType]>[];
  return {
    calls: breakdowns.reduce((sum, bd) => sum + bd.callCount, 0),
    inputTokens: breakdowns.reduce((sum, bd) => sum + bd.inputTokens, 0),
    outputTokens: breakdowns.reduce((sum, bd) => sum + bd.outputTokens, 0),
    audioSeconds: row.breakdown.voice_minutes?.units ?? row.breakdown.transcribe_stt?.units ?? 0,
    photoBytes: row.breakdown.photo_upload?.units ?? 0,
    pdfRuns: (row.breakdown.pdf_export?.callCount ?? 0) + (row.breakdown.cover_pdf_export?.callCount ?? 0),
    failedJobs: row.breakdown.job_failed?.callCount ?? 0,
  };
}

function formatOperationUnits(op: OperationType, units: number): string | null {
  if (!units) return null;
  if (op === 'transcribe_stt' || op === 'voice_minutes') return formatMinutes(units);
  if (op === 'photo_upload') return formatMegabytes(units);
  return `${formatInt(units)} units`;
}

function OperationCell({ row, op }: { row: UserUsageRow; op: OperationType }) {
  const bd = row.breakdown[op];
  if (!bd) return <>—</>;
  const units = formatOperationUnits(op, bd.units);
  const tokenLine = bd.inputTokens || bd.outputTokens
    ? `${formatInt(bd.inputTokens)} in / ${formatInt(bd.outputTokens)} out`
    : null;

  return (
    <div className="min-w-[7rem]">
      <div className="font-semibold">{formatUsd(bd.costUsd)}</div>
      <div className="text-[11px] text-stone-500 leading-snug">
        {formatInt(bd.callCount)} calls
        {units ? ` · ${units}` : ''}
      </div>
      {tokenLine && <div className="text-[11px] text-stone-500 leading-snug">{tokenLine}</div>}
      {bd.lastModel && <div className="text-[10px] text-stone-400 leading-snug truncate max-w-[9rem]" title={bd.lastModel}>{bd.lastModel}</div>}
    </div>
  );
}

function utcNow() {
  return new Date();
}

function getRecentMonths(): string[] {
  const now = utcNow();
  return Array.from({ length: 4 }, (_, i) => {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (3 - i), 1));
    return d.toISOString().slice(0, 7);
  });
}

function monthsInYear(year: number): string[] {
  const now = utcNow();
  const currentYM = now.getUTCFullYear() * 12 + now.getUTCMonth();
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(Date.UTC(year, i, 1));
    return d.toISOString().slice(0, 7);
  }).filter(m => {
    const [y, mo] = m.split('-').map(Number);
    return y * 12 + (mo - 1) <= currentYM;
  });
}

function monthsInRange(from: string, to: string): string[] {
  const months: string[] = [];
  const [fy, fm] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  let y = fy, m = fm;
  while (y < ty || (y === ty && m <= tm)) {
    months.push(`${y}-${String(m).padStart(2, '0')}`);
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return months;
}

function aggregateResponses(responses: UsageResponse[]): UserUsageRow[] {
  const byUser: Record<string, UserUsageRow> = {};
  for (const resp of responses) {
    for (const row of resp.users) {
      if (!byUser[row.sub]) {
        byUser[row.sub] = {
          sub: row.sub,
          fullSub: row.fullSub,
          displayName: row.displayName,
          month: 'multi',
          totalCostUsd: 0,
          breakdown: {},
        };
      }
      byUser[row.sub].displayName = byUser[row.sub].displayName ?? row.displayName;
      byUser[row.sub].fullSub = byUser[row.sub].fullSub ?? row.fullSub;
      byUser[row.sub].totalCostUsd += row.totalCostUsd;
      for (const [op, bd] of Object.entries(row.breakdown) as [OperationType, NonNullable<UserUsageRow['breakdown'][OperationType]>][]) {
        const existing = byUser[row.sub].breakdown[op];
        if (!existing) {
          byUser[row.sub].breakdown[op] = { ...bd };
        } else {
          existing.callCount    += bd.callCount;
          existing.inputTokens  += bd.inputTokens;
          existing.outputTokens += bd.outputTokens;
          existing.units        += bd.units;
          existing.costUsd      += bd.costUsd;
          existing.lastModel     = bd.lastModel ?? existing.lastModel;
        }
      }
    }
  }
  return Object.values(byUser).sort((a, b) => b.totalCostUsd - a.totalCostUsd);
}

async function fetchMonths(months: string[]): Promise<UserUsageRow[]> {
  const responses = await Promise.all(months.map(m => fetchUsage(m)));
  return aggregateResponses(responses);
}

export function Admin() {
  const [identity, setIdentity]       = useState<UserIdentity | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [activeTab, setActiveTab]     = useState<AdminTab>('usage');

  const now = utcNow();
  const utcYear = now.getUTCFullYear();
  const recentMonths = getRecentMonths();
  const thisMonth = recentMonths[recentMonths.length - 1];
  const lastMonth = recentMonths[recentMonths.length - 2];

  // Usage tab state
  const [filterMode, setFilterMode]   = useState<FilterMode>('this-month');
  const [customFrom, setCustomFrom]   = useState<string>(`${utcYear}-01`);
  const [customTo, setCustomTo]       = useState<string>(thisMonth);
  const [appliedFrom, setAppliedFrom] = useState<string>(customFrom);
  const [appliedTo, setAppliedTo]     = useState<string>(customTo);
  const [rows, setRows]               = useState<UserUsageRow[] | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError]   = useState<string | null>(null);
  const [cartesiaCredits, setCartesiaCredits]   = useState<CartesiaCreditsResponse | null>(null);
  const [cartesiaLoading, setCartesiaLoading]   = useState(false);
  const [cartesiaError, setCartesiaError]       = useState<string | null>(null);

  // Beta tab state
  const [signups, setSignups]         = useState<BetaSignup[] | null>(null);
  const [betaLoading, setBetaLoading] = useState(false);
  const [betaError, setBetaError]     = useState<string | null>(null);
  const [selected, setSelected]       = useState<Set<string>>(new Set());
  const [approving, setApproving]     = useState(false);
  const [approveMsg, setApproveMsg]   = useState<string | null>(null);

  useEffect(() => {
    getUserIdentity()
      .then(id => { setIdentity(id); setAuthLoading(false); })
      .catch(() => setAuthLoading(false));
  }, []);

  // Load Cartesia credits once on mount (usage tab)
  useEffect(() => {
    if (authLoading || !identity || identity.sub !== ADMIN_SUB) return;
    setCartesiaLoading(true);
    fetchCartesiaCredits()
      .then(setCartesiaCredits)
      .catch(e => setCartesiaError(e instanceof Error ? e.message : String(e)))
      .finally(() => setCartesiaLoading(false));
  }, [identity, authLoading]);

  // Load usage data when filter changes
  useEffect(() => {
    if (authLoading || !identity || identity.sub !== ADMIN_SUB) return;
    setUsageLoading(true);
    setUsageError(null);
    setRows(null);

    let months: string[];
    if (filterMode === 'this-month') months = [thisMonth];
    else if (filterMode === 'last-month') months = [lastMonth];
    else if (filterMode === 'this-year') months = monthsInYear(utcYear);
    else if (filterMode === 'last-year') months = monthsInYear(utcYear - 1);
    else months = monthsInRange(appliedFrom, appliedTo);

    fetchMonths(months)
      .then(setRows)
      .catch(e => setUsageError(e instanceof Error ? e.message : String(e)))
      .finally(() => setUsageLoading(false));
  }, [filterMode, appliedFrom, appliedTo, identity, authLoading]);

  // Load beta signups when switching to Beta tab
  useEffect(() => {
    if (activeTab !== 'beta' || !identity || identity.sub !== ADMIN_SUB) return;
    if (signups !== null) return; // already loaded
    setBetaLoading(true);
    setBetaError(null);
    fetchBetaSignups()
      .then(setSignups)
      .catch(e => setBetaError(e instanceof Error ? e.message : String(e)))
      .finally(() => setBetaLoading(false));
  }, [activeTab, identity]);

  if (!authLoading && identity && identity.sub !== ADMIN_SUB) {
    return <Navigate to="/" replace />;
  }
  if (authLoading) return null;

  const pillCls = (active: boolean) =>
    active
      ? 'bg-amber-500 text-white font-semibold px-4 py-2 rounded-full text-xs'
      : 'bg-white border border-stone-200 text-stone-700 px-4 py-2 rounded-full text-xs hover:bg-stone-50';

  const tabCls = (tab: AdminTab) =>
    activeTab === tab
      ? 'px-5 py-2.5 text-sm font-semibold border-b-2 border-amber-500 text-amber-700'
      : 'px-5 py-2.5 text-sm text-stone-500 hover:text-stone-700 border-b-2 border-transparent';

  async function handleApprove() {
    const emails = [...selected];
    if (!emails.length) return;
    setApproving(true);
    setApproveMsg(null);
    try {
      const result = await approveBetaUsers(emails);
      setSelected(new Set());
      setSignups(null); // reload
      setBetaLoading(true);
      fetchBetaSignups()
        .then(setSignups)
        .finally(() => setBetaLoading(false));

      const parts: string[] = [];
      if (result.approved.length) parts.push(`Approved ${result.approved.length} user(s).`);
      if (result.errors.length) parts.push(`${result.errors.length} error(s): ${result.errors.map(e => e.email).join(', ')}`);
      setApproveMsg(parts.join(' '));
    } catch (e) {
      setApproveMsg(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setApproving(false);
    }
  }

  const pendingSignups = signups?.filter(s => s.status === 'pending') ?? [];
  const allSelected = pendingSignups.length > 0 && pendingSignups.every(s => selected.has(s.email));

  function toggleAll() {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(pendingSignups.map(s => s.email)));
    }
  }

  function toggleOne(email: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  }

  return (
    <main className="min-h-dvh bg-stone-50 flex flex-col items-center py-12 px-4">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-sm border border-stone-200">

        <header className="px-8 pt-8 pb-0">
          <h1 className="text-3xl font-serif text-stone-900">Admin</h1>
        </header>

        {/* Tab bar */}
        <div className="flex border-b border-stone-200 px-8 mt-4">
          <button type="button" onClick={() => setActiveTab('usage')} className={tabCls('usage')}>
            Usage
          </button>
          <button type="button" onClick={() => setActiveTab('beta')} className={tabCls('beta')}>
            Beta Signups
            {signups !== null && signups.filter(s => s.status === 'pending').length > 0 && (
              <span className="ml-2 bg-amber-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                {signups.filter(s => s.status === 'pending').length}
              </span>
            )}
          </button>
        </div>

        <div className="p-8">

          {/* ── Usage tab ── */}
          {activeTab === 'usage' && (
            <>
              <div className="mb-6">
                <CartesiaCreditsWidget loading={cartesiaLoading} data={cartesiaCredits} error={cartesiaError} />
              </div>

              <div className="flex flex-wrap gap-2 items-center mb-6">
                <button type="button" onClick={() => setFilterMode('this-month')} className={pillCls(filterMode === 'this-month')}>
                  This Month
                </button>
                <button type="button" onClick={() => setFilterMode('last-month')} className={pillCls(filterMode === 'last-month')}>
                  Last Month
                </button>
                <button type="button" onClick={() => setFilterMode('this-year')} className={pillCls(filterMode === 'this-year')}>
                  This Year ({utcYear})
                </button>
                <button type="button" onClick={() => setFilterMode('last-year')} className={pillCls(filterMode === 'last-year')}>
                  Last Year ({utcYear - 1})
                </button>
                <span className="text-stone-300">|</span>
                <input
                  type="month" value={customFrom} max={customTo}
                  onChange={e => setCustomFrom(e.target.value)}
                  className="px-3 py-1.5 text-xs border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <span className="text-xs text-stone-400">to</span>
                <input
                  type="month" value={customTo} min={customFrom} max={thisMonth}
                  onChange={e => setCustomTo(e.target.value)}
                  className="px-3 py-1.5 text-xs border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <button
                  type="button"
                  onClick={() => { setAppliedFrom(customFrom); setAppliedTo(customTo); setFilterMode('custom'); }}
                  className={pillCls(filterMode === 'custom')}
                >
                  Apply Range
                </button>
              </div>

              {usageError && (
                <div className="text-red-700 bg-red-50 border border-red-200 rounded p-3 text-sm mb-4">
                  Failed to load usage data. Check network and try again.
                </div>
              )}
              {usageLoading && <p className="text-stone-500 text-sm">Loading usage data...</p>}
              {!usageLoading && !usageError && rows && rows.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-stone-500 text-sm font-semibold">No usage data for this period.</p>
                </div>
              )}
              {!usageLoading && !usageError && rows && rows.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left border-collapse">
                    <thead>
                      <tr>
                        <th className="text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3 border-b border-stone-200">User</th>
                        <th className="text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3 border-b border-stone-200">Total (USD)</th>
                        <th className="text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3 border-b border-stone-200">Calls</th>
                        <th className="text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3 border-b border-stone-200">Tokens</th>
                        <th className="text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3 border-b border-stone-200">Voice Min</th>
                        <th className="text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3 border-b border-stone-200">Photo MB</th>
                        <th className="text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3 border-b border-stone-200">PDFs</th>
                        <th className="text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3 border-b border-stone-200">Failures</th>
                        {OPERATION_COLUMNS.map(col => (
                          <th key={col.key} className="text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3 border-b border-stone-200">
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, idx) => {
                        const totals = getTotals(row);
                        const isZero = row.totalCostUsd === 0;
                        const cellBase = isZero
                          ? 'px-4 py-3 text-stone-400 tabular-nums border-b border-stone-100'
                          : 'px-4 py-3 text-stone-900 tabular-nums border-b border-stone-100';
                        return (
                          <tr key={idx} className="hover:bg-stone-50">
                            <td className={cellBase}>
                              <div className="font-semibold">{row.displayName ?? row.sub}</div>
                              <div className="text-[11px] text-stone-500 font-mono" title={row.fullSub ?? row.sub}>{row.sub}</div>
                            </td>
                            <td className={`${cellBase} font-semibold`}>{formatUsd(row.totalCostUsd)}</td>
                            <td className={cellBase}>{formatInt(totals.calls)}</td>
                            <td className={cellBase}>
                              <div>{formatInt(totals.inputTokens)} in</div>
                              <div className="text-[11px] text-stone-500">{formatInt(totals.outputTokens)} out</div>
                            </td>
                            <td className={cellBase}>{formatMinutes(totals.audioSeconds)}</td>
                            <td className={cellBase}>{formatMegabytes(totals.photoBytes)}</td>
                            <td className={cellBase}>{formatInt(totals.pdfRuns)}</td>
                            <td className={totals.failedJobs > 0 ? `${cellBase} text-red-700 font-semibold` : cellBase}>{formatInt(totals.failedJobs)}</td>
                            {OPERATION_COLUMNS.map(col => (
                              <td key={col.key} className={cellBase}>
                                <OperationCell row={row} op={col.key} />
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* ── Beta tab ── */}
          {activeTab === 'beta' && (
            <>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-stone-500">
                  {signups === null ? '' : `${signups.length} total · ${signups.filter(s => s.status === 'pending').length} pending`}
                </p>
                <div className="flex items-center gap-3">
                  {approveMsg && (
                    <span className="text-sm text-stone-600">{approveMsg}</span>
                  )}
                  <button
                    type="button"
                    disabled={selected.size === 0 || approving}
                    onClick={handleApprove}
                    className="px-4 py-2 text-sm font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {approving ? 'Approving…' : `Approve Selected (${selected.size})`}
                  </button>
                </div>
              </div>

              {betaError && (
                <div className="text-red-700 bg-red-50 border border-red-200 rounded p-3 text-sm mb-4">
                  {betaError}
                </div>
              )}

              {betaLoading && <p className="text-stone-500 text-sm">Loading signups...</p>}

              {!betaLoading && !betaError && signups && signups.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-stone-400 text-sm">No beta signups yet.</p>
                </div>
              )}

              {!betaLoading && !betaError && signups && signups.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left border-collapse">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 border-b border-stone-200 w-8">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleAll}
                            className="rounded border-stone-300 text-amber-500 focus:ring-amber-400"
                          />
                        </th>
                        <th className="text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3 border-b border-stone-200">Name</th>
                        <th className="text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3 border-b border-stone-200">Email</th>
                        <th className="text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3 border-b border-stone-200">Platforms</th>
                        <th className="text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3 border-b border-stone-200">Why they want in</th>
                        <th className="text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3 border-b border-stone-200">Signed up</th>
                        <th className="text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3 border-b border-stone-200">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {signups.map(signup => {
                        const isPending = signup.status === 'pending';
                        const isChecked = selected.has(signup.email);
                        return (
                          <tr key={signup.email} className={isChecked ? 'bg-amber-50' : 'hover:bg-stone-50'}>
                            <td className="px-4 py-3 border-b border-stone-100">
                              {isPending && (
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => toggleOne(signup.email)}
                                  className="rounded border-stone-300 text-amber-500 focus:ring-amber-400"
                                />
                              )}
                            </td>
                            <td className="px-4 py-3 border-b border-stone-100 font-medium text-stone-900">
                              {signup.name || <span className="text-stone-400 italic">—</span>}
                            </td>
                            <td className="px-4 py-3 border-b border-stone-100 text-stone-700">{signup.email}</td>
                            <td className="px-4 py-3 border-b border-stone-100 text-stone-600 whitespace-nowrap">
                              {formatPlatforms(signup.platforms) || <span className="text-stone-400 italic">—</span>}
                            </td>
                            <td className="px-4 py-3 border-b border-stone-100 text-stone-600 max-w-xs">
                              {signup.reason || <span className="text-stone-400 italic">—</span>}
                            </td>
                            <td className="px-4 py-3 border-b border-stone-100 text-stone-500 tabular-nums whitespace-nowrap">
                              {new Date(signup.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3 border-b border-stone-100">
                              <StatusBadge status={signup.status} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

        </div>
      </div>
    </main>
  );
}

function formatPlatforms(platforms?: string[]): string {
  const labels: Record<string, string> = { ios: 'iOS', android: 'Android' };
  return (platforms ?? []).map(platform => labels[platform] ?? platform).join(', ');
}

function StatusBadge({ status }: { status: BetaSignup['status'] }) {
  if (status === 'pending') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">Pending</span>;
  }
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">Approved</span>;
}

function CartesiaCreditsWidget({
  loading, data, error,
}: {
  loading: boolean;
  data: CartesiaCreditsResponse | null;
  error: string | null;
}) {
  if (loading) {
    return (
      <div className="bg-stone-50 border border-stone-200 rounded-xl px-5 py-4 flex items-center gap-3">
        <div className="w-2.5 h-2.5 rounded-full bg-stone-300 animate-pulse" />
        <span className="text-sm text-stone-500">Loading Cartesia credits…</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 text-sm text-amber-800">
        Could not load Cartesia credits — API key may lack usage permission.{' '}
        <a href="https://play.cartesia.ai/subscription" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-900">
          Check on Cartesia dashboard
        </a>
      </div>
    );
  }

  const { creditsUsed, month } = data;
  const isHigh    = creditsUsed > 95_000;
  const isWarning = creditsUsed > 80_000;
  const dotColor   = isHigh ? 'bg-red-500'   : isWarning ? 'bg-amber-400' : 'bg-emerald-400';
  const textColor  = isHigh ? 'text-red-700' : isWarning ? 'text-amber-700' : 'text-stone-700';
  const wrapperCls = isHigh ? 'border-red-200 bg-red-50' : isWarning ? 'border-amber-200 bg-amber-50' : 'border-stone-200 bg-stone-50';

  return (
    <div className={`rounded-xl border px-5 py-4 flex items-center justify-between gap-4 ${wrapperCls}`}>
      <div className="flex items-center gap-3">
        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dotColor}`} />
        <div>
          <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Cartesia TTS — {month}</p>
          <p className={`text-xl font-semibold tabular-nums mt-0.5 ${textColor}`}>
            {creditsUsed.toLocaleString()} credits used
          </p>
          {isHigh && (
            <p className="text-xs text-red-600 mt-0.5">Near limit — upgrade or enable overages to restore voice</p>
          )}
        </div>
      </div>
      <a href="https://play.cartesia.ai/subscription" target="_blank" rel="noopener noreferrer" className="text-xs text-stone-500 hover:text-stone-700 underline flex-shrink-0">
        Manage plan
      </a>
    </div>
  );
}
